'use client';

import { useTranslation } from 'next-i18next';
import { PhoneIcon } from '@heroicons/react/24/solid';
import {
  Anchor, Chip, PopoverStyled, PopoverStyledButton, PopoverStyledPanel,
} from '@components/common';
import { WhatsAppIcon } from '@components/common/Icons';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { voicePhoneDisplay, voicePhoneTelHref, whatsAppHref } from '@utils/contactPhone';

type ContactChannel = 'call' | 'whatsapp';

/**
 * Contact intent from the navbar, tracked as its own GA4 event — fired on the chosen option, not on
 * opening the menu, so `channel: 'call'` keeps the meaning the event had when the chip dialled directly.
 *
 * `surface` is constant today — one component serves both breakpoints — and is sent
 * anyway so the parameter shape stays stable if a second placement ever appears.
 */
const trackContactPhoneClick = (channel: ContactChannel) => {
  sendGoogleTagEvent('nav_contact_phone_click', 'Custom', { surface: 'navbar', channel });
};

/**
 * @description The voice contact number in the navbar, beside the filters icon. Tapping it opens a
 * small menu: **Call** dials the voice line, **WhatsApp** opens a chat with the WhatsApp line. No link
 * can offer both on its own — `tel:` always opens the dialer, `wa.me` always opens WhatsApp — so the
 * choice is ours to present.
 *
 * The two options point at different numbers in production (see `@utils/contactPhone`); the chip
 * shows the voice one. The WhatsApp option is dropped when no WhatsApp number is configured, and the
 * whole chip renders nothing when no voice number is, rather than a chip with a bare `+` in it.
 *
 * `wa.me` directly, not the `/wa` campaign route: a server-side hop can stop iOS from handing the
 * link to the WhatsApp app, and the GA event already covers attribution here.
 *
 * `Anchor`, not `LinkStyled`: the latter runs the href through the i18n path helper, which prefixes
 * the active locale to anything it does not recognise as a translated route — turning `tel:+40…`
 * into `/entel:+40…` on English pages. Every other phone CTA in the app uses a plain anchor for the
 * same reason.
 */
export const ContactPhoneChip = () => {
  const { t } = useTranslation('common');

  const telHref = voicePhoneTelHref();
  const waHref = whatsAppHref();
  if (!telHref) return null;

  const optionClasses = 'flex items-center gap-3 px-4 py-3 text-sm font-medium text-ink hover:bg-surface-100';

  return (
    <PopoverStyled>
      {({ close }) => (
        <>
          <PopoverStyledButton className="flex cursor-pointer outline-none">
            {/* nowrap: the grouped number must not break across lines in the tight mobile row. */}
            <Chip className="whitespace-nowrap border-surface-100 bg-surface-100">
              {voicePhoneDisplay()}
            </Chip>
          </PopoverStyledButton>

          <PopoverStyledPanel position="left" animation="slideDown" className="top-full mt-1 min-w-44 overflow-hidden py-1">
            <Anchor
              styling="none"
              href={telHref}
              className={optionClasses}
              // Fire-and-forget: the event must not delay or block the handset taking over.
              onClick={() => {
                trackContactPhoneClick('call');
                close();
              }}
            >
              <PhoneIcon className="size-5 text-primary" />
              {t('navbar.contact.call')}
            </Anchor>

            {waHref && (
              <Anchor
                styling="none"
                href={waHref}
                // Desktop has no WhatsApp handler to take over the tab — WhatsApp Web opens in a new one.
                target="_blank"
                rel="noopener noreferrer"
                className={optionClasses}
                onClick={() => {
                  trackContactPhoneClick('whatsapp');
                  close();
                }}
              >
                <WhatsAppIcon className="size-5 text-social-whatsapp" />
                {t('navbar.contact.whatsapp')}
              </Anchor>
            )}
          </PopoverStyledPanel>
        </>
      )}
    </PopoverStyled>
  );
};
